import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent, Button, Box, Typography, Chip, IconButton, Collapse, Divider, LinearProgress, List, ListItem, Dialog, DialogContent } from '@mui/material';
import { Close, ExpandMore, ExpandLess, ZoomIn, ZoomOut } from '@mui/icons-material';
import { Nesting, Part, Path } from '../services/productionService';
import api from '../services/api';

interface NestingCardProps {
  nesting: Nesting;
}

const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
  switch (status) {
    case 'IN_PROGRESS': return 'info';
    case 'COMPLETED': return 'success';
    case 'ERROR': return 'error';
    case 'CANCELLED': return 'warning';
    default: return 'default';
  }
};

const statusLabel = (status: string) => ({ IN_PROGRESS: 'In corso', COMPLETED: 'Completato', ERROR: 'Errore', CANCELLED: 'Annullato' }[status] || status);

const formatTime = (date?: string) => {
  if (!date) return '-';
  return new Date(date).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
};

const NestingImage: React.FC<{ nestingId: string; code: string }> = ({ nestingId, code }) => {
  const [src, setSrc] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ startX: number; startY: number; panX: number; panY: number } | null>(null);

  useEffect(() => {
    let revoke: string | null = null;
    api.get(`/production/nestings/${nestingId}/image`, { responseType: 'blob' })
      .then((res) => {
        const url = URL.createObjectURL(res.data);
        revoke = url;
        setSrc(url);
      })
      .catch(() => setSrc(null));
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [nestingId]);

  const handleOpen = () => { setZoom(1); setPan({ x: 0, y: 0 }); setOpen(true); };

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.stopPropagation();
    setZoom((z) => Math.min(8, Math.max(0.5, z + (e.deltaY < 0 ? 0.3 : -0.3))));
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y };
  }, [pan]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current) return;
    setPan({
      x: dragRef.current.panX + (e.clientX - dragRef.current.startX),
      y: dragRef.current.panY + (e.clientY - dragRef.current.startY),
    });
  }, []);

  const handlePointerUp = useCallback(() => { dragRef.current = null; }, []);

  if (!src) return null;

  return (
    <>
      <Box role="button" tabIndex={0} aria-label={`Ingrandisci anteprima ${code}`} sx={{ mb: 2, textAlign: 'center', cursor: 'pointer' }} onClick={handleOpen} onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); handleOpen(); } }}>
        <Box
          component="img"
          src={src}
          alt={`Nesting ${code}`}
          sx={{
            maxWidth: '100%',
            maxHeight: 300,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': { opacity: 0.85 },
          }}
        />
        <Typography variant="caption" color="text.secondary">Clicca per ingrandire</Typography>
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth={false} fullWidth
        PaperProps={{ sx: { bgcolor: 'black', m: 1, maxHeight: '95vh' } }}
      >
        <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10, display: 'flex', gap: 0.5 }}>
          <IconButton aria-label="Aumenta zoom" size="small" sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)' }} onClick={() => setZoom((z) => Math.min(8, z + 0.5))}>
            <ZoomIn />
          </IconButton>
          <IconButton aria-label="Riduci zoom" size="small" sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)' }} onClick={() => setZoom((z) => Math.max(0.5, z - 0.5))}>
            <ZoomOut />
          </IconButton>
          <IconButton aria-label="Chiudi anteprima" size="small" sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)' }} onClick={() => setOpen(false)}>
            <Close />
          </IconButton>
        </Box>
        <DialogContent
          sx={{ p: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', cursor: zoom > 1 ? 'grab' : 'default', touchAction: 'none' }}
          onWheel={handleWheel}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
        >
          <Box
            component="img"
            src={src}
            alt={`Nesting ${code}`}
            draggable={false}
            sx={{
              transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
              transition: dragRef.current ? 'none' : 'transform 0.15s ease',
              maxWidth: '100%',
              maxHeight: '90vh',
              userSelect: 'none',
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

const NestingCard: React.FC<NestingCardProps> = ({ nesting }) => {
  const [expanded, setExpanded] = useState(false);

  const progress = nesting.totalParts > 0
    ? Math.round((nesting.partsCompleted / nesting.totalParts) * 100)
    : 0;

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <Box>
            <Typography variant="h6">{nesting.code}</Typography>
            <Typography variant="caption" color="text.secondary">
              {nesting.machine?.name} • {nesting.material || 'N/A'}
              {nesting.thickness != null && ` • ${nesting.thickness}mm`}
              {nesting.sheetLength != null && nesting.sheetWidth != null && ` • ${nesting.sheetLength}×${nesting.sheetWidth}mm`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip label={statusLabel(nesting.status)} color={getStatusColor(nesting.status)} size="small" />
            <Button size="small" aria-expanded={expanded} aria-controls={`nesting-details-${nesting.id}`} onClick={() => setExpanded(value => !value)} endIcon={expanded ? <ExpandLess /> : <ExpandMore />}>Dettagli</Button>
          </Box>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {nesting.partsCompleted}/{nesting.totalParts} parti completate ({progress}%)
          </Typography>
          <LinearProgress variant="determinate" value={progress} sx={{ mt: 0.5 }} />
        </Box>

        <Box sx={{ mt: 1, display: 'flex', gap: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Inizio: {formatTime(nesting.startedAt)}
          </Typography>
          {nesting.completedAt && (
            <Typography variant="caption" color="text.secondary">
              Fine: {formatTime(nesting.completedAt)}
            </Typography>
          )}
        </Box>
      </CardContent>

      <Collapse in={expanded} id={`nesting-details-${nesting.id}`}>
        <Divider />
        <CardContent>
          {nesting.nestingImagePath && (
            <NestingImage nestingId={nesting.id} code={nesting.code} />
          )}
          {nesting.parts && nesting.parts.length > 0 ? (
            <List>
              {nesting.parts.map((part, partIndex) => (
                <PartItem key={part.id} part={part} partIndex={partIndex} />
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">Nessuna parte registrata</Typography>
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
};

const PartItem: React.FC<{ part: Part; partIndex: number }> = ({ part, partIndex }) => {
  const [expanded, setExpanded] = useState(false);
  const partNumber = part.sequenceNumber ?? (partIndex + 1);

  return (
    <Box sx={{ mb: 1, pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
      <Box
        role="button" tabIndex={0} aria-expanded={expanded} aria-label={`Dettagli parte ${partNumber}`}
        onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setExpanded(value => !value); } }}
        onClick={() => setExpanded(!expanded)}
        sx={{ display: 'flex', justifyContent: 'space-between', cursor: 'pointer', py: 1 }}
      >
        <Box>
          <Typography variant="subtitle2" fontWeight="bold">Parte {partNumber}</Typography>
          <Typography variant="caption" color="text.secondary">{part.code}</Typography>
          <br />
          <Typography variant="caption" color="text.secondary">
            {part.pathsCompleted}/{part.totalPaths} tracciati •
            {formatTime(part.startedAt)} - {formatTime(part.completedAt)}
          </Typography>
        </Box>
        <Chip label={statusLabel(part.status)} color={getStatusColor(part.status)} size="small" />
      </Box>

      <Collapse in={expanded}>
        {part.paths && part.paths.length > 0 ? (
          <List dense sx={{ pl: 2 }}>
            {part.paths.map((path, pathIndex) => (
              <PathItem key={path.id} path={path} pathIndex={pathIndex} />
            ))}
          </List>
        ) : (
          <Typography variant="caption" color="text.secondary">Nessun tracciato</Typography>
        )}
      </Collapse>
    </Box>
  );
};

const PathItem: React.FC<{ path: Path; pathIndex: number }> = ({ path, pathIndex }) => {
  const pathNumber = path.sequenceNumber ?? (pathIndex + 1);

  return (
    <ListItem sx={{ display: 'flex', gap: 2, py: 0.5, px: 0 }}>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2">Contorno {pathNumber} • {path.code}</Typography>
        <Typography variant="caption" color="text.secondary">
          {formatTime(path.startedAt)} → {formatTime(path.completedAt)}
        </Typography>
      </Box>
      {path.laserPower && (
        <Chip label={`${path.laserPower}%`} size="small" variant="outlined" />
      )}
      {path.feedRate && (
        <Chip label={`${path.feedRate} mm/min`} size="small" variant="outlined" />
      )}
      <Chip label={statusLabel(path.status)} color={getStatusColor(path.status)} size="small" />
    </ListItem>
  );
};

export default NestingCard;
